import type { SelectedPurchaseOffer, TicketPurchaseClass } from "../../store/ephemeral/slices/ticketPurchase.slice";
import type { ItineraryDTO } from "../../types/dtos/api/itinerary/itinerary.dto";
import type { PrebookingItemDTO, SelectedOfferDTO } from "../../types/dtos/api/purchase/purchase-prebook.dto";
import type { CffSalesArea, CffSalesOffer } from "../../types/dtos/api/sales/cff-sales-offer.dto";
import type { SwissCffProviderOffer } from "../../types/dtos/api/sales/provider-offer.dto";

/**
 * Pure helpers for the ticket purchase wizard: CFF-segment derivations shared by
 * the modal (segment/class defaults), the selection step (offer classification)
 * and the order summary (prebook payload, labels).
 */

/** `productId` of the CFF journey ticket ("Billet de parcours") — the only product City-Ticket addons attach to. */
export const CFF_JOURNEY_TICKET_PRODUCT_ID = 125;

/** Prefix carried by CFF city-zone labels ("Suppl. City Genève"). */
export const CFF_CITY_AREA_LABEL_PREFIX = "Suppl. City ";

export type CffCityAddonKind = "leading" | "tailing" | "both";

/** CFF offer enriched with its position inside the segment's filtered `swiss_cff` bundle list. */
export interface IndexedCffOffer extends CffSalesOffer {
    "bundleIndex": number;
    "offerIndex":  number;
}

/** True for the journey ticket — the exclusive base offer City-Ticket addons attach to. */
export function isCffJourneyTicket( offer: Pick<CffSalesOffer, "productId" | "offerType"> ): boolean {
    return offer.productId === CFF_JOURNEY_TICKET_PRODUCT_ID && offer.offerType === "TRANSPORT";
}

/**
 * Classifies a City-Ticket addon from its `validityArea`: `leading`, `tailing`
 * or `both` when it covers both city zones. Returns `null` for non-city offers.
 *
 * Detection is structural — a TRANSPORT offer carrying a city validity area is a
 * City-Ticket — so no city `productId` is hardcoded and future city products are
 * handled automatically.
 *
 * @param offer - Candidate offer.
 * @returns The addon kind, or `null`.
 */
export function classifyCffCityTicket( offer: CffSalesOffer ): CffCityAddonKind | null {
    if ( offer.offerType !== "TRANSPORT" ) {
        return null;
    }

    const hasLeading = offer.validityArea?.leadingArea !== undefined;
    const hasTailing = offer.validityArea?.tailingArea !== undefined;

    if ( hasLeading && hasTailing ) {
        return "both";
    }

    if ( hasLeading ) {
        return "leading";
    }

    if ( hasTailing ) {
        return "tailing";
    }

    return null;
}

/** True for a City-Ticket offer — a journey-ticket supplement, never a standalone base offer. */
export function isCffCityTicket( offer: CffSalesOffer ): boolean {
    return classifyCffCityTicket( offer ) !== null;
}

/**
 * Strips the CFF "Suppl. City " prefix from a city-zone label.
 *
 * @param area - City validity area of an offer.
 * @returns The bare city label, or `null` when absent.
 */
export function getCffCityAreaLabel( area?: CffSalesArea ): string | null {
    if ( area?.areaLabel === undefined ) {
        return null;
    }

    return area.areaLabel.replace( CFF_CITY_AREA_LABEL_PREFIX, "" ).trim();
}

/**
 * Display label of a base offer: "Billet de parcours" for the journey ticket,
 * the capitalized title prefix otherwise (or the whole title without colon).
 *
 * @param title - Raw offer title.
 * @param productId - Offer product id.
 * @returns Display label.
 */
export function formatCffOfferLabel( title: string, productId: number ): string {
    if ( productId === CFF_JOURNEY_TICKET_PRODUCT_ID ) {
        return "Billet de parcours";
    }

    const firstPart = title.split( ":" )[0]?.trim() ?? title;

    if ( firstPart.length === 0 ) {
        return title;
    }

    return `${ firstPart.charAt( 0 ).toUpperCase() }${ firstPart.slice( 1 ) }`;
}

/**
 * Displayable fare conditions of an offer ("Remboursable", "Échangeable").
 * Powers the condition badges currently hidden behind `SHOW_OFFER_CONDITION_BADGES`.
 *
 * @param offer - Offer to describe.
 * @returns Condition labels.
 */
export function listCffOfferConditions( offer: Pick<CffSalesOffer, "refundable" | "exchangeable"> ): string[] {
    const conditions = [ offer.refundable === true ? "Remboursable" : "Non remboursable" ];

    if ( offer.exchangeable === true ) {
        conditions.push( "Échangeable" );
    }

    return conditions;
}

/** Result of scanning an itinerary for CFF-purchasable segments. */
export interface CffSegmentDefaults {

    /** Selected travel class per segment id (defaults to the user's pricing preference). */
    "segmentClass": Record<string, TicketPurchaseClass>;

    /** Skip flag per segment id (none skipped by default). */
    "skipSegment": Record<string, boolean>;
}

/**
 * Lists the `swiss_cff`-purchasable segment ids of an itinerary.
 *
 * @param itinerary - Itinerary to scan.
 * @returns Segment ids that expose at least one `swiss_cff` provider offer.
 */
export function listPurchasableCffSegmentIds( itinerary: ItineraryDTO ): string[] {
    return Object.entries( itinerary.offer?.segments ?? {} ).
        filter( ( [ , segment ] ) => segment.providerOffers?.some( ( offer ) => offer.provider === "swiss_cff" ) === true ).
        map( ( [ segmentId ] ) => segmentId );
}

/**
 * Computes the initial `segmentClass`/`skipSegment` maps for the selection step.
 *
 * @param itinerary - Itinerary being purchased.
 * @param defaultClass - Travel class from the user's pricing preferences.
 * @returns Initial per-segment maps.
 */
export function buildCffSegmentDefaults( itinerary: ItineraryDTO, defaultClass: TicketPurchaseClass ): CffSegmentDefaults {
    const segmentClass: Record<string, TicketPurchaseClass> = {};
    const skipSegment: Record<string, boolean> = {};

    for ( const segmentId of listPurchasableCffSegmentIds( itinerary ) ) {
        segmentClass[segmentId] = defaultClass;
        skipSegment[segmentId] = false;
    }

    return {
        segmentClass,
        skipSegment,
    };
}

/**
 * Resolves the CFF conversation id attached to the itinerary offers.
 *
 * The conversation id is stored on the first `swiss_cff` provider properties
 * found; CFF offers within one pricing response share the same conversation.
 *
 * @param itinerary - Itinerary being purchased.
 * @returns Conversation id, or an empty string when absent.
 */
export function resolveCffConversationId( itinerary: ItineraryDTO ): string {
    return Object.values( itinerary.offer?.segments ?? {} ).
        find( ( segment ) => segment.providerProperties?.swiss_cff?.conversationId !== undefined )?.
        providerProperties?.swiss_cff?.conversationId ?? "";
}

/**
 * Maps the wizard's selected offers onto the `SelectedOfferDTO` prebook payload.
 *
 * @param itinerary - Itinerary holding the provider offer bundles.
 * @param selectedOffers - Per-segment wizard selection.
 * @returns Prebook-ready offer list.
 */
export function buildSelectedOfferDtos(
    itinerary: ItineraryDTO,
    selectedOffers: Record<string, SelectedPurchaseOffer>,
): SelectedOfferDTO[] {
    return Object.values( selectedOffers ).map( ( selection ) => {
        const segment = itinerary.offer?.segments[selection.segmentId];
        const bundle = segment?.providerOffers?.[selection.bundleIndex] as SwissCffProviderOffer | undefined;
        const offer = bundle?.offers[selection.offerIndex];

        return {
            "segmentId":        selection.segmentId,
            "offerIdentifier":  selection.offerIdentifier,
            "productId":        offer?.productId ?? 0,
            "qualityOfService": offer?.qualityOfService !== undefined ? Number( offer.qualityOfService ) : undefined,
            "price":            offer?.price ?? 0,
            "passengerId":      offer?.passengerId ?? "p1",
        };
    } );
}

/**
 * Re-binds a wizard selection onto a refreshed itinerary by position.
 *
 * Offer identifiers change when an offer is refreshed (expired or repriced),
 * but the `bundleIndex`/`offerIndex` inside the `swiss_cff` bundle list of each
 * segment still point at the same offer — so the shopper's choice survives the
 * refresh and lands back on the updated summary instead of the selection step.
 *
 * @param itinerary - Itinerary with freshly loaded offers.
 * @param selectedOffers - Selection made on the previous offers.
 * @returns The selection with updated offer identifiers, or `null` when a
 *   segment's offer structure changed and the choice cannot be remapped.
 */
export function remapSelectionToRefreshedOffers(
    itinerary: ItineraryDTO,
    selectedOffers: Record<string, SelectedPurchaseOffer>,
): Record<string, SelectedPurchaseOffer> | null {
    const remapped: Record<string, SelectedPurchaseOffer> = {};

    for ( const selection of Object.values( selectedOffers ) ) {
        const segment = itinerary.offer?.segments[selection.segmentId];
        const bundles = ( segment?.providerOffers ?? [] ).
            filter( ( offer ): offer is SwissCffProviderOffer => offer.provider === "swiss_cff" );
        const offer = bundles[selection.bundleIndex]?.offers[selection.offerIndex];

        if ( offer?.offerIdentifier === undefined ) {
            return null;
        }

        remapped[selection.segmentId] = {
            ...selection,
            "offerIdentifier": offer.offerIdentifier,
        };
    }

    return remapped;
}

/** Selection maps rebuilt from prebooking items, used by the Payrexx return resume. */
export interface RebuiltPurchaseSelection {
    "selectedOffers": Record<string, SelectedPurchaseOffer>;
    "segmentClass":   Record<string, TicketPurchaseClass>;
    "skipSegment":    Record<string, boolean>;
}

/**
 * Rebuilds the wizard selection (`selectedOffers`, `segmentClass`, `skipSegment`)
 * from the prebooked offers of a resumed purchase attempt.
 *
 * `bundleIndex`/`offerIndex` are recovered by matching each prebooking's
 * `offerIdentifier` against the `swiss_cff` bundles of the resumed itinerary
 * (same indexing convention as `TicketSegmentSelection`, i.e. the index inside
 * the filtered `swiss_cff` bundle list). CFF-purchasable segments absent from
 * the prebooking are restored as skipped, matching the choice made at order
 * time.
 *
 * @param itinerary - Itinerary carried by the resume snapshot.
 * @param prebookings - Prebooking items (`segmentId` + `offerIdentifier`).
 * @param defaultClass - Fallback travel class for unpurchased segments.
 * @returns Selection maps ready to write into `ticketPurchaseSlice`.
 */
export function rebuildSelectionFromPrebookings(
    itinerary: ItineraryDTO,
    prebookings: PrebookingItemDTO[],
    defaultClass: TicketPurchaseClass,
): RebuiltPurchaseSelection {
    const { segmentClass, skipSegment } = buildCffSegmentDefaults( itinerary, defaultClass );
    const selectedOffers: Record<string, SelectedPurchaseOffer> = {};
    const prebookedSegmentIds = new Set( prebookings.map( ( item ) => item.segmentId ) );

    /*
     * A purchasable segment absent from the prebooking was skipped by the user
     * when the order was placed — restore that choice.
     */
    for ( const segmentId of listPurchasableCffSegmentIds( itinerary ) ) {
        skipSegment[segmentId] = !prebookedSegmentIds.has( segmentId );
    }

    for ( const prebooking of prebookings ) {
        const segment = itinerary.offer?.segments[prebooking.segmentId];
        const bundles = ( segment?.providerOffers ?? [] ).
            filter( ( offer ): offer is SwissCffProviderOffer => offer.provider === "swiss_cff" );

        bundles.forEach( ( bundle, bundleIndex ) => {
            bundle.offers.forEach( ( offer, offerIndex ) => {
                if ( offer.offerIdentifier !== prebooking.offerIdentifier ) {
                    return;
                }

                selectedOffers[prebooking.segmentId] = {
                    "segmentId":       prebooking.segmentId,
                    bundleIndex,
                    offerIndex,
                    "offerIdentifier": prebooking.offerIdentifier,
                };
                segmentClass[prebooking.segmentId] = String( offer.qualityOfService ) === "1" ? "1" : "2";
            } );
        } );
    }

    return {
        selectedOffers,
        segmentClass,
        skipSegment,
    };
}

/** A City-Ticket supplement proposed under the journey-ticket row. */
export interface CffCityAddonOption {

    /** Which city zone(s) the supplement covers. */
    "kind": CffCityAddonKind;

    /** Bare city label(s), e.g. "Genève" or "Genève et Bern". */
    "label": string;

    /** The offer to select when the supplement is checked. */
    "offer": IndexedCffOffer;
}

/**
 * Selection view-model of one CFF segment: the base offers rendered as rows and
 * the City-Ticket supplements attached to the journey ticket.
 */
export interface CffOfferSelectionModel {

    /** Selectable base offers (non-city TRANSPORT of the current class, price ascending). */
    "mainOffers": IndexedCffOffer[];

    /** The journey ticket — exclusive base of City-Ticket supplements; `null` when absent. */
    "parcoursOffer": IndexedCffOffer | null;

    /** Offer preselected by default: the cheapest base offer; `null` when none. */
    "defaultOffer": IndexedCffOffer | null;

    /** City supplements available in the current class, `null` when not proposed. */
    "leadingAddon": CffCityAddonOption | null;
    "tailingAddon": CffCityAddonOption | null;
    "bothAddon":    CffCityAddonOption | null;

    /** Kind of the currently selected City supplement, `null` when the selection is not a city offer. */
    "selectedCityKind": CffCityAddonKind | null;
}

const buildCityAddonLabel = ( offer: CffSalesOffer, kind: CffCityAddonKind ): string | null => {
    if ( kind === "leading" ) {
        return getCffCityAreaLabel( offer.validityArea?.leadingArea );
    }

    if ( kind === "tailing" ) {
        return getCffCityAreaLabel( offer.validityArea?.tailingArea );
    }

    const leadingLabel = getCffCityAreaLabel( offer.validityArea?.leadingArea );
    const tailingLabel = getCffCityAreaLabel( offer.validityArea?.tailingArea );

    if ( leadingLabel !== null && tailingLabel !== null ) {
        return `${ leadingLabel } et ${ tailingLabel }`;
    }

    return leadingLabel ?? tailingLabel;
};

/**
 * Builds the selection view-model of a segment for the current travel class.
 *
 * Base offers are every non-city `TRANSPORT` offer of the class, price ascending.
 * City-Ticket supplements are detected structurally through `validityArea` and
 * attached exclusively to the journey ticket (`parcoursOffer`).
 *
 * @param offers - All segment offers indexed by their `swiss_cff` bundle position.
 * @param currentClass - Travel class currently displayed.
 * @param selection - Current wizard selection of the segment, if any.
 * @returns The selection view-model.
 */
export function buildCffOfferSelectionModel(
    offers: IndexedCffOffer[],
    currentClass: TicketPurchaseClass,
    selection: SelectedPurchaseOffer | undefined,
): CffOfferSelectionModel {
    const transportOffers = offers.
        filter( ( offer ) => offer.offerType === "TRANSPORT" && String( offer.qualityOfService ) === currentClass ).
        sort( ( a, b ) => a.price - b.price );

    const mainOffers = transportOffers.filter( ( offer ) => !isCffCityTicket( offer ) );
    const parcoursOffer = mainOffers.find( isCffJourneyTicket ) ?? null;
    const defaultOffer = mainOffers[0] ?? null;

    const addons: CffCityAddonOption[] = [];

    for ( const offer of transportOffers ) {
        const kind = classifyCffCityTicket( offer );

        if ( kind === null ) {
            continue;
        }

        const label = buildCityAddonLabel( offer, kind );

        if ( label === null ) {
            continue;
        }

        addons.push( {
            kind,
            label,
            offer,
        } );
    }

    const selectedOffer = selection === undefined
        ? undefined
        : offers.find( ( o ) => o.bundleIndex === selection.bundleIndex && o.offerIndex === selection.offerIndex );

    return {
        mainOffers,
        parcoursOffer,
        defaultOffer,
        "leadingAddon":     addons.find( ( a ) => a.kind === "leading" ) ?? null,
        "tailingAddon":     addons.find( ( a ) => a.kind === "tailing" ) ?? null,
        "bothAddon":        addons.find( ( a ) => a.kind === "both" ) ?? null,
        "selectedCityKind": selectedOffer === undefined ? null : classifyCffCityTicket( selectedOffer ),
    };
}

/**
 * Resolves the offer to select when a City supplement checkbox is toggled.
 *
 * The combined `both` offer wins when both zones are wanted and it exists;
 * otherwise the single-zone offers apply; unchecking everything falls back to
 * the bare journey ticket. Returns `null` when no valid target exists.
 *
 * @param model - Selection view-model of the segment.
 * @param kind - Checkbox being toggled.
 * @returns The offer to select, or `null`.
 */
export function resolveCityAddonSelection(
    model: CffOfferSelectionModel,
    kind: "leading" | "tailing",
): IndexedCffOffer | null {
    const isLeadingChecked = model.selectedCityKind === "leading" || model.selectedCityKind === "both";
    const isTailingChecked = model.selectedCityKind === "tailing" || model.selectedCityKind === "both";

    const wantsLeading = kind === "leading" ? !isLeadingChecked : isLeadingChecked;
    const wantsTailing = kind === "tailing" ? !isTailingChecked : isTailingChecked;

    if ( wantsLeading && wantsTailing && model.bothAddon !== null ) {
        return model.bothAddon.offer;
    }

    if ( wantsLeading && model.leadingAddon !== null ) {
        return model.leadingAddon.offer;
    }

    if ( wantsTailing && model.tailingAddon !== null ) {
        return model.tailingAddon.offer;
    }

    return model.parcoursOffer;
}
